import type {
  OrgContractVersionOwnerView,
  OrgContractAcceptanceOwnerView,
} from "@bw/shared/types/generated";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { OwnerFeeTerms } from "./OwnerFeeTerms";
import { ExactAgreementDocument } from "./ExactAgreementDocument";

export interface OwnerContractHistoryProps {
  versions: readonly OrgContractVersionOwnerView[];
  acceptances: readonly OrgContractAcceptanceOwnerView[];
}

/** Receipts describe their own version; upcoming versions have no accept action. */
export function OwnerContractHistory({
  versions,
  acceptances,
}: OwnerContractHistoryProps) {
  return (
    <Stack spacing={2} divider={<Divider />}>
      {versions.map((version) => (
        <Stack spacing={1} key={version.version_id}>
          <Typography variant="subtitle2">
            Version {version.version_number} — effective{" "}
            {version.effective_date}
          </Typography>
          <OwnerFeeTerms terms={version} />
          <ExactAgreementDocument document={version.document} />
        </Stack>
      ))}
      {acceptances.map((receipt) => {
        const version = versions.find(
          (value) => value.version_id === receipt.version_id,
        );
        return (
          <Stack key={receipt.acceptance_id} spacing={1}>
            <Typography variant="body2">
              Accepted
              {version
                ? ` version ${version.version_number}`
                : " terms"} on {new Date(receipt.accepted_at).toLocaleString()}.
            </Typography>
            <OwnerFeeTerms terms={receipt} />
            <ExactAgreementDocument
              document={receipt.document}
              unavailableCopy="This legacy acceptance has economic history but no retained exact document copy."
            />
          </Stack>
        );
      })}
      {!versions.length && !acceptances.length ? (
        <Typography color="text.secondary">
          No contract history returned.
        </Typography>
      ) : null}
    </Stack>
  );
}
